import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { mfiApi } from "../../api";

const COLORS = {
  aquaBlue: "#11a8a4",
  grassGreen: "#7ed957",
  darkGrey: "#3c3b3f",
  vividPurple: "#9440dd",
  white: "#ffffff",
  lightGrey: "#f3f4f6",
  mediumGrey: "#e5e7eb",
  darkText: "#1f2937",
  mediumText: "#6b7280",
  lightText: "#9ca3af",
};

export default function MfiClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [expandedClientId, setExpandedClientId] = useState(null);
  const [packagesByClient, setPackagesByClient] = useState({});
  const [loadingPackages, setLoadingPackages] = useState({});
  const [packagesError, setPackagesError] = useState({});

  const loadClients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await mfiApi.getMfiClients();
      const mfiOnly = Array.isArray(data) ? data.filter((c) => c.is_mfi !== false) : [];
      // If is_mfi filter removes all but API already returns only MFI, fallback to all
      setClients(mfiOnly.length > 0 ? mfiOnly : data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load MFI clients");
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const handleClientClick = async (client) => {
    const cid = client.client_id;
    if (expandedClientId === cid) {
      setExpandedClientId(null);
      return;
    }
    setExpandedClientId(cid);
    if (packagesByClient[cid] || loadingPackages[cid]) return;
    setLoadingPackages((p) => ({ ...p, [cid]: true }));
    setPackagesError((p) => ({ ...p, [cid]: null }));
    try {
      const pkgs = await mfiApi.getMfiPackages(cid);
      setPackagesByClient((prev) => ({ ...prev, [cid]: pkgs }));
    } catch (e) {
      setPackagesError((p) => ({ ...p, [cid]: e?.response?.data?.detail || e.message || "Failed to load packages" }));
      setPackagesByClient((prev) => ({ ...prev, [cid]: [] }));
    } finally {
      setLoadingPackages((p) => ({ ...p, [cid]: false }));
    }
  };

  const handlePackageClick = (clientId, pkg) => {
    navigate(`/coordinator/mfi/families/${encodeURIComponent(clientId)}/${encodeURIComponent(pkg.id)}`);
  };

  const handleReportClick = (e, clientId, pkg) => {
    e.stopPropagation();
    navigate(`/coordinator/mfi/report-mfi/${encodeURIComponent(clientId)}/${encodeURIComponent(pkg.id)}`);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: COLORS.lightGrey }}>
      {/* Header */}
      <div style={{ backgroundColor: COLORS.white, padding: "16px 24px", borderBottom: `1px solid ${COLORS.mediumGrey}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={() => navigate("/dashboard")}
              style={{ backgroundColor: COLORS.lightGrey, color: COLORS.darkText, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              Back
            </button>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: COLORS.darkText, margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: `${COLORS.grassGreen}20`, display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.grassGreen }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
              </span>
              MFI Clients
            </h1>
          </div>
          {/* <p style={{ color: COLORS.mediumText, margin: "6px 0 0 48px", fontSize: 13 }}>Select a client to view its packages. Data from <code style={{ background: COLORS.lightGrey, padding: "1px 6px", borderRadius: 4 }}>/mfi-clients/</code></p> */}
        </div>
        <button onClick={loadClients} style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 6, color: COLORS.darkText }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          Refresh
        </button>
      </div>

      <div style={{ flex: 1, padding: 24, maxWidth: 1100, width: "100%", margin: "0 auto", boxSizing: "border-box" }}>
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 40, backgroundColor: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.mediumGrey}` }}>
            <div style={{ width: 36, height: 36, border: `4px solid ${COLORS.mediumGrey}`, borderTop: `4px solid ${COLORS.aquaBlue}`, borderRadius: "50%", animation: "spin 1s linear infinite" }} />
            <p style={{ marginTop: 12, color: COLORS.mediumText, fontSize: 14 }}>Loading MFI clients...</p>
          </div>
        ) : error ? (
          <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: 20, textAlign: "center" }}>
            <p style={{ color: "#dc2626", fontSize: 14, margin: 0 }}>{error}</p>
            <button onClick={loadClients} style={{ marginTop: 12, backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "8px 16px", cursor: "pointer", fontSize: 13 }}>Retry</button>
          </div>
        ) : clients.length === 0 ? (
          <div style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 12, padding: 40, textAlign: "center" }}>
            <p style={{ color: COLORS.mediumText, margin: 0 }}>No MFI clients found.</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {/* Table header for desktop */}
            <div style={{ display: "none" }} className="mfi-desktop-header" />
            <div style={{ backgroundColor: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.mediumGrey}`, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead>
                    <tr style={{ backgroundColor: COLORS.lightGrey, textAlign: "left", color: COLORS.mediumText }}>
                      <th style={{ padding: "12px 16px", fontWeight: 600, whiteSpace: "nowrap" }}>Client ID</th>
                      <th style={{ padding: "12px 16px", fontWeight: 600 }}>Name</th>
                      <th style={{ padding: "12px 16px", fontWeight: 600, display: "none" }} className="hide-mobile">Email</th>
                      <th style={{ padding: "12px 16px", fontWeight: 600 }}>Type</th>
                      <th style={{ padding: "12px 16px", fontWeight: 600, textAlign: "right" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((client) => {
                      const isExpanded = expandedClientId === client.client_id;
                      const pkgs = packagesByClient[client.client_id] || [];
                      const isLoadingPkg = !!loadingPackages[client.client_id];
                      const pkgErr = packagesError[client.client_id];
                      return (
                        <React.Fragment key={client.client_id}>
                          <tr
                            onClick={() => handleClientClick(client)}
                            style={{
                              borderTop: `1px solid ${COLORS.mediumGrey}`,
                              cursor: "pointer",
                              backgroundColor: isExpanded ? `${COLORS.aquaBlue}08` : COLORS.white,
                              transition: "background 0.15s",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${COLORS.aquaBlue}08`)}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isExpanded ? `${COLORS.aquaBlue}08` : COLORS.white)}
                          >
                            <td style={{ padding: "14px 16px", fontWeight: 600, color: COLORS.aquaBlue, whiteSpace: "nowrap" }}>{client.client_id}</td>
                            <td style={{ padding: "14px 16px", fontWeight: 500, color: COLORS.darkText }}>
                              {client.name}
                              <span style={{ marginLeft: 8, fontSize: 11, backgroundColor: client.is_mfi ? `${COLORS.grassGreen}20` : COLORS.lightGrey, color: client.is_mfi ? COLORS.grassGreen : COLORS.mediumText, padding: "2px 6px", borderRadius: 10, fontWeight: 600 }}>
                                {client.is_mfi ? "MFI" : client.login_type || "Client"}
                              </span>
                              <div style={{ fontSize: 12, color: COLORS.mediumText, marginTop: 2, display: "block" }} className="show-mobile-email">{client.email}</div>
                            </td>
                            <td style={{ padding: "14px 16px", color: COLORS.mediumText, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} className="hide-mobile-cell">{client.email}</td>
                            <td style={{ padding: "14px 16px", color: COLORS.mediumText }}>{client.login_type || "—"}</td>
                            <td style={{ padding: "14px 16px", textAlign: "right" }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: isExpanded ? COLORS.aquaBlue : COLORS.mediumText, fontWeight: 500, fontSize: 13 }}>
                                {isExpanded ? "Hide" : "View Packages"}
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                              </span>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={5} style={{ padding: 0, backgroundColor: `${COLORS.aquaBlue}04`, borderTop: `1px solid ${COLORS.mediumGrey}` }}>
                                <div style={{ padding: 20 }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: COLORS.darkText }}>Packages for {client.name} ({client.client_id})</h3>
                                    <span style={{ fontSize: 12, color: COLORS.mediumText }}>{isLoadingPkg ? "Loading..." : `${pkgs.length} package${pkgs.length !== 1 ? "s" : ""}`}</span>
                                  </div>
                                  {isLoadingPkg ? (
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 16, backgroundColor: COLORS.white, borderRadius: 8, border: `1px solid ${COLORS.mediumGrey}` }}>
                                      <div style={{ width: 20, height: 20, border: `3px solid ${COLORS.mediumGrey}`, borderTop: `3px solid ${COLORS.aquaBlue}`, borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                                      <span style={{ fontSize: 13, color: COLORS.mediumText }}>Loading packages...</span>
                                    </div>
                                  ) : pkgErr ? (
                                    <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                                      <span style={{ color: "#dc2626", fontSize: 13 }}>{pkgErr}</span>
                                      <button onClick={() => handleClientClick(client)} style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 12 }}>Retry</button>
                                    </div>
                                  ) : pkgs.length === 0 ? (
                                    <div style={{ backgroundColor: COLORS.white, border: `1px dashed ${COLORS.mediumGrey}`, borderRadius: 8, padding: 20, textAlign: "center", color: COLORS.mediumText, fontSize: 13 }}>No packages found for this client.</div>
                                  ) : (
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
                                      {pkgs.map((pkg) => (
                                        <div
                                          key={pkg.id}
                                          onClick={() => handlePackageClick(client.client_id, pkg)}
                                          style={{
                                            backgroundColor: COLORS.white,
                                            border: `1px solid ${COLORS.mediumGrey}`,
                                            borderRadius: 10,
                                            padding: 16,
                                            cursor: "pointer",
                                            transition: "all 0.15s",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 8,
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = COLORS.aquaBlue;
                                            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = COLORS.mediumGrey;
                                            e.currentTarget.style.boxShadow = "none";
                                          }}
                                        >
                                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                                            <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: COLORS.darkText, flex: 1 }}>{pkg.name}</h4>
                                            <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 10, backgroundColor: pkg.is_completed ? `${COLORS.darkGrey}15` : `${COLORS.grassGreen}15`, color: pkg.is_completed ? COLORS.darkGrey : COLORS.grassGreen, whiteSpace: "nowrap" }}>
                                              {pkg.is_completed ? "Completed" : "Active"}
                                            </span>
                                          </div>
                                          <div style={{ fontSize: 12, color: COLORS.mediumText, lineHeight: 1.6 }}>
                                            <div><strong style={{ color: COLORS.darkText }}>ID:</strong> {pkg.id} &nbsp;·&nbsp; <strong style={{ color: COLORS.darkText }}>Price:</strong> ₹{Number(pkg.price).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
                                            <div><strong style={{ color: COLORS.darkText }}>Camp:</strong> {pkg.camp ?? "—"} &nbsp;·&nbsp; <strong style={{ color: COLORS.darkText }}>Lang:</strong> {pkg.report_language || "—"}</div>
                                            <div><strong style={{ color: COLORS.darkText }}>Start:</strong> {pkg.start_date || "—"} &nbsp;→&nbsp; <strong style={{ color: COLORS.darkText }}>End:</strong> {pkg.end_date || "—"}</div>
                                          </div>
                                          <div style={{ marginTop: 4, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                                            <button
                                              onClick={(e) => handleReportClick(e, client.client_id, pkg)}
                                              style={{ fontSize: 12, fontWeight: 600, backgroundColor: COLORS.vividPurple, color: COLORS.white, border: "none", borderRadius: 6, padding: "7px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                                            >
                                              Report
                                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                            </button>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.aquaBlue, display: "flex", alignItems: "center", gap: 4 }}>
                                              View Patients
                                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
      <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}} @media(max-width:640px){.hide-mobile-cell{display:none}} @media(min-width:641px){.show-mobile-email{display:none !important}}`}</style>
    </div>
  );
}
